import { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Group,
  Loader,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { MonthPickerInput } from "@mantine/dates";
import dayjs from "dayjs";
import "dayjs/locale/ru";
import { UnderbookedValue } from "./UnderbookedValue";
import {
  fetchMonthlyReport,
  getReportXlsxUrl,
  PERSONNEL_CATEGORY_OPTIONS,
  type PersonnelCategory,
  type MonthlyReport,
  type MonthlyReportQuery,
  type MonthlyReportWeekStatus,
  type MonthlyWeekStatus,
} from "../api/client";
import { useReportFetch } from "../hooks/useReportFetch";
import { REPORT_ABSENCE_TYPE_LABEL, employeeOptionsFromRows } from "./reportMeta";

dayjs.locale("ru");

// ---------------------------------------------------------------------------
// Helpers (экспортируются для unit-тестов)
// ---------------------------------------------------------------------------

/**
 * Возвращает предыдущий календарный месяц относительно `today`.
 *  - today = 2026-04-15 → {year: 2026, month: 3}
 *  - today = 2026-01-05 → {year: 2025, month: 12}
 *
 * `month` — 1-based (1..12) для совместимости с API `YYYY-MM`.
 */
export function getLastMonthBefore(today: Date = new Date()): { year: number; month: number } {
  let year = today.getFullYear();
  let month = today.getMonth(); // 0-based текущий
  if (month === 0) {
    year -= 1;
    month = 12;
  }
  return { year, month };
}

export function monthToIso({ year, month }: { year: number; month: number }): string {
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}`;
}

function dateToMonth(d: Date | null): { year: number; month: number } | null {
  if (!d) return null;
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
}

function monthToDate({ year, month }: { year: number; month: number }): Date {
  return new Date(year, month - 1, 1);
}

// Цветовая схема точек «Недели» (см. design.md D8).
interface DotStyle {
  bg: string; // Mantine color token (e.g. "teal.6") или CSS
  border?: string;
  icon?: string; // emoji/символ для absence_only
  empty?: boolean;
}

function weekDotStyle(status: MonthlyWeekStatus): DotStyle {
  switch (status) {
    case "approved":
      return { bg: "var(--mantine-color-teal-6)" };
    case "draft":
    case "rework_requested_by_line_manager":
    case "rework_requested_by_project_manager":
      return { bg: "var(--mantine-color-yellow-6)" };
    case "submitted_to_line_manager":
    case "waiting_project_managers":
      return { bg: "var(--mantine-color-blue-6)" };
    case "absence_only":
      return { bg: "var(--mantine-color-gray-6)", icon: "🏖" };
    case "rolled_back_by_admin":
      return { bg: "var(--mantine-color-orange-6)" };
    case "timesheet_exempt":
      // Заполненный серый: пустой кружок читается как «провалил месяц».
      return { bg: "var(--mantine-color-gray-4)" };
    case "no_week":
    default:
      return { bg: "transparent", border: "var(--mantine-color-gray-4)", empty: true };
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function MonthlySummaryView() {
  const defaultMonth = useMemo(() => getLastMonthBefore(), []);
  const [selectedDate, setSelectedDate] = useState<Date | null>(monthToDate(defaultMonth));
  const [employeeId, setEmployeeId] = useState<string | null>(null);
  const [projectId, setProjectId] = useState<string | null>(null);
  // Категория персонала: отделяет тех, кто зарабатывает на проектах, от тех,
  // кто обеспечивает работу компании. Пусто = как раньше.
  const [category, setCategory] = useState<string | null>(null);

  const query: MonthlyReportQuery | null = useMemo(() => {
    const m = dateToMonth(selectedDate);
    if (!m) return null;
    return {
      month: monthToIso(m),
      ...(employeeId ? { employeeId: Number(employeeId) } : {}),
      ...(projectId ? { projectId: Number(projectId) } : {}),
      ...(category ? { category: category as PersonnelCategory } : {}),
    };
  }, [selectedDate, employeeId, projectId, category]);

  const { report, loading, error } = useReportFetch(query, fetchMonthlyReport);

  const employeeOptions = useMemo(() => {
    if (!report) return [];
    return employeeOptionsFromRows(report.rows);
  }, [report]);
  const projectOptions = useMemo(
    () =>
      report
        ? report.projects.map((p) => ({ value: String(p.id), label: `${p.code} — ${p.name}` }))
        : [],
    [report],
  );

  return (
    <Stack gap="md">
      <Title order={3}>Месячный свод</Title>
      <Text size="xs" c="dimmed">
        Отображается ваша зона ответственности за календарный месяц. Максимум 100 сотрудников на
        страницу.
      </Text>
      <Group align="flex-end" gap="sm">
        <MonthPickerInput
          label="Месяц"
          value={selectedDate}
          onChange={(v) => {
            if (!v) {
              setSelectedDate(null);
              return;
            }
            const d = v instanceof Date ? v : new Date(v);
            setSelectedDate(d);
          }}
          valueFormat="MMMM YYYY"
          w={200}
        />
        <Select
          label="Сотрудник"
          placeholder="Все"
          clearable
          searchable
          data={employeeOptions}
          value={employeeId}
          onChange={setEmployeeId}
          w={260}
          nothingFoundMessage="Нет совпадений"
        />
        <Select
          label="Проект"
          placeholder="Все"
          clearable
          searchable
          data={projectOptions}
          value={projectId}
          onChange={setProjectId}
          w={260}
          nothingFoundMessage="Нет совпадений"
        />
        <Select
          label="Категория персонала"
          placeholder="Все"
          clearable
          data={PERSONNEL_CATEGORY_OPTIONS}
          value={category}
          onChange={(value) => {
            setCategory(value);
            // Сбрасываем сотрудника: он мог не подходить под новую категорию,
            // и тогда таблица пустела, а имя продолжало висеть в фильтре —
            // без единого объяснения, почему строк нет.
            setEmployeeId(null);
          }}
          w={220}
        />
        <Button
          component="a"
          href={
            query
              ? getReportXlsxUrl({
                  kind: "monthly",
                  month: query.month,
                  employeeId: query.employeeId,
                  projectId: query.projectId,
                  // Файл обязан совпадать с экраном: без категории выгрузка
                  // отдавала другой набор строк под тем же именем.
                  category: query.category,
                })
              : undefined
          }
          disabled={loading || !query}
          variant="default"
          aria-label="Экспорт в Excel"
        >
          Экспорт в Excel
        </Button>
      </Group>

      {loading && <Loader size="sm" />}
      {error && (
        <Alert color="red" title="Не удалось загрузить отчёт">
          {error}
        </Alert>
      )}
      {!loading && !error && report && <MonthlyReportTable report={report} />}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Table
// ---------------------------------------------------------------------------

interface MonthlyReportTableProps {
  report: MonthlyReport;
}

function MonthlyReportTable({ report }: MonthlyReportTableProps) {
  if (report.rows.length === 0) {
    return <Text c="dimmed">В выбранном месяце никто не списывал часы и не отсутствовал.</Text>;
  }
  const { projects, absence_types: absenceTypes, rows, totals } = report;

  return (
    <ScrollArea>
      <Table withTableBorder highlightOnHover striped>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Сотрудник</Table.Th>
            <Table.Th>Недели</Table.Th>
            {projects.map((p) => (
              <Table.Th key={p.id} ta="right" title={p.name}>
                {p.code}
              </Table.Th>
            ))}
            {absenceTypes.map((t) => (
              <Table.Th key={t} ta="right">
                {REPORT_ABSENCE_TYPE_LABEL[t] ?? t}
              </Table.Th>
            ))}
            <Table.Th ta="right">Нераспределено</Table.Th>
            <Table.Th ta="right">Итого</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row) => {
            const projectHoursMap = new Map<number, string>();
            for (const ph of row.project_hours) {
              projectHoursMap.set(ph.project_id, ph.hours);
            }
            return (
              <Table.Tr key={row.employee.id}>
                <Table.Td>{row.employee.full_name}</Table.Td>
                <Table.Td>
                  <WeeksBreakdown items={row.weeks_breakdown} />
                </Table.Td>
                {projects.map((p) => (
                  <Table.Td key={p.id} ta="right">
                    {projectHoursMap.get(p.id) ?? "—"}
                  </Table.Td>
                ))}
                {absenceTypes.map((t) => (
                  <Table.Td key={t} ta="right">
                    {row.absence_hours_by_type[t] ?? "0.00"}
                  </Table.Td>
                ))}
                <Table.Td ta="right">
                  <UnderbookedValue
                    value={row.unallocated_hours}
                    danger={Number(row.unallocated_hours) > 0}
                  />
                </Table.Td>
                <Table.Td ta="right">{row.total_hours}</Table.Td>
              </Table.Tr>
            );
          })}
          {/* Итого */}
          <Table.Tr>
            <Table.Td fw={700}>Итого</Table.Td>
            <Table.Td></Table.Td>
            {projects.map((p) => {
              const cell = totals.project_hours.find((x) => x.project_id === p.id);
              return (
                <Table.Td key={p.id} ta="right" fw={700}>
                  {cell ? cell.hours : "0.00"}
                </Table.Td>
              );
            })}
            {absenceTypes.map((t) => (
              <Table.Td key={t} ta="right" fw={700}>
                {totals.absence_hours_by_type[t] ?? "0.00"}
              </Table.Td>
            ))}
            <Table.Td ta="right" fw={700}>
              <UnderbookedValue
                value={totals.unallocated_hours}
                danger={Number(totals.unallocated_hours) > 0}
                bold
              />
            </Table.Td>
            <Table.Td ta="right" fw={700}>
              {totals.total_hours}
            </Table.Td>
          </Table.Tr>
        </Table.Tbody>
      </Table>
    </ScrollArea>
  );
}

// ---------------------------------------------------------------------------
// WeeksBreakdown — колонка с цветными точками + tooltip
// ---------------------------------------------------------------------------

interface WeeksBreakdownProps {
  items: MonthlyReportWeekStatus[];
}

function WeeksBreakdown({ items }: WeeksBreakdownProps) {
  return (
    <Group gap={4} wrap="nowrap">
      {items.map((w) => {
        const style = weekDotStyle(w.status);
        const labelDate = dayjs(w.week_start).format("DD.MM.YYYY");
        return (
          <Tooltip key={w.week_start} label={`${labelDate} • ${w.status_display}`} withArrow>
            <span
              role="img"
              aria-label={`${labelDate}: ${w.status_display}`}
              data-status={w.status}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 16,
                height: 16,
                borderRadius: "50%",
                background: style.bg,
                border: style.border ? `1px solid ${style.border}` : "none",
                fontSize: 10,
                lineHeight: 1,
                cursor: "default",
                userSelect: "none",
              }}
            >
              {style.icon ?? ""}
            </span>
          </Tooltip>
        );
      })}
    </Group>
  );
}
